import React from 'react';
import { Document, Page, Text, View, StyleSheet, pdf, Font } from '@react-pdf/renderer';

// Register fonts for better typography
Font.register({
  family: 'Helvetica',
  fonts: [
    { src: 'https://cdn.jsdelivr.net/npm/@canvas-fonts/helvetica@1.0.4/Helvetica.ttf' },
    { src: 'https://cdn.jsdelivr.net/npm/@canvas-fonts/helvetica@1.0.4/Helvetica-Bold.ttf', fontWeight: 'bold' },
  ]
});

// PDF Styles
const styles = StyleSheet.create({
  page: {
    padding: 50,
    paddingBottom: 70,
    fontSize: 10,
    fontFamily: 'Helvetica',
    lineHeight: 1.5,
  },
  header: {
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: '#EAB308',
  },
  logo: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  logoYi: {
    color: '#EAB308',
  },
  logoBooking: {
    color: '#1F2937',
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 11,
    color: '#6B7280',
  },
  tocTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 15,
    marginTop: 10,
  },
  tocItem: {
    fontSize: 11,
    color: '#4B5563',
    marginBottom: 8,
    paddingLeft: 10,
  },
  tocItemNumber: {
    color: '#EAB308',
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 25,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  sectionNumber: {
    color: '#EAB308',
  },
  subsectionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#374151',
    marginTop: 18,
    marginBottom: 8,
  },
  paragraph: {
    fontSize: 10,
    color: '#4B5563',
    marginBottom: 10,
    textAlign: 'justify',
  },
  bold: {
    fontWeight: 'bold',
    color: '#1F2937',
  },
  highlight: {
    backgroundColor: '#FEF3C7',
    padding: 2,
  },
  bulletList: {
    marginLeft: 15,
    marginBottom: 10,
  },
  bulletItem: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  bullet: {
    width: 15,
    color: '#EAB308',
    fontWeight: 'bold',
  },
  bulletText: {
    flex: 1,
    fontSize: 10,
    color: '#4B5563',
  },
  numberedItem: {
    flexDirection: 'row',
    marginBottom: 6,
    marginLeft: 10,
  },
  numberedNumber: {
    width: 20,
    fontSize: 10,
    color: '#EAB308',
    fontWeight: 'bold',
  },
  numberedText: {
    flex: 1,
    fontSize: 10,
    color: '#4B5563',
  },
  table: {
    marginTop: 10,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tableHeaderCell: {
    flex: 1,
    padding: 8,
    fontSize: 9,
    fontWeight: 'bold',
    color: '#374151',
    borderRightWidth: 1,
    borderRightColor: '#E5E7EB',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tableCell: {
    flex: 1,
    padding: 8,
    fontSize: 9,
    color: '#4B5563',
    borderRightWidth: 1,
    borderRightColor: '#E5E7EB',
  },
  tableCellLast: {
    borderRightWidth: 0,
  },
  note: {
    backgroundColor: '#FEF3C7',
    padding: 12,
    marginVertical: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#EAB308',
  },
  noteTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#92400E',
    marginBottom: 4,
  },
  noteText: {
    fontSize: 9,
    color: '#92400E',
  },
  tip: {
    backgroundColor: '#ECFDF5',
    padding: 12,
    marginVertical: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#10B981',
  },
  tipTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#065F46',
    marginBottom: 4,
  },
  tipText: {
    fontSize: 9,
    color: '#065F46',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 50,
    right: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 10,
  },
  footerText: {
    fontSize: 8,
    color: '#9CA3AF',
  },
  pageNumber: {
    fontSize: 8,
    color: '#6B7280',
  },
  pageBreak: {
    marginTop: 30,
  },
});

// Helper Components
const Bullet = ({ children }) => (
  <View style={styles.bulletItem}>
    <Text style={styles.bullet}>•</Text>
    <Text style={styles.bulletText}>{children}</Text>
  </View>
);

const NumberedItem = ({ number, children }) => (
  <View style={styles.numberedItem}>
    <Text style={styles.numberedNumber}>{number}.</Text>
    <Text style={styles.numberedText}>{children}</Text>
  </View>
);

const Table = ({ headers, rows }) => (
  <View style={styles.table}>
    <View style={styles.tableHeader}>
      {headers.map((header, i) => (
        <Text key={i} style={[styles.tableHeaderCell, i === headers.length - 1 && styles.tableCellLast]}>
          {header}
        </Text>
      ))}
    </View>
    {rows.map((row, rowIndex) => (
      <View key={rowIndex} style={styles.tableRow}>
        {row.map((cell, cellIndex) => (
          <Text key={cellIndex} style={[styles.tableCell, cellIndex === row.length - 1 && styles.tableCellLast]}>
            {cell}
          </Text>
        ))}
      </View>
    ))}
  </View>
);

const Note = ({ title, children }) => (
  <View style={styles.note}>
    <Text style={styles.noteTitle}>{title || 'Note:'}</Text>
    <Text style={styles.noteText}>{children}</Text>
  </View>
);

const Tip = ({ children }) => (
  <View style={styles.tip}>
    <Text style={styles.tipTitle}>💡 Tip:</Text>
    <Text style={styles.tipText}>{children}</Text>
  </View>
);

// Main PDF Document
const UserGuidePDF = () => (
  <Document>
    {/* Cover Page */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.logo}>
          <Text style={styles.logoYi}>yi</Text>
          <Text style={styles.logoBooking}>Booking</Text>
        </Text>
      </View>
      
      <View style={{ marginTop: 60, alignItems: 'center' }}>
        <Text style={{ fontSize: 28, fontWeight: 'bold', color: '#1F2937', marginBottom: 15 }}>
          Fixed Calendar Admin
        </Text>
        <Text style={{ fontSize: 28, fontWeight: 'bold', color: '#EAB308', marginBottom: 30 }}>
          User Guide
        </Text>
        <Text style={{ fontSize: 12, color: '#6B7280', marginBottom: 5 }}>
          Complete guide for managing your booking calendar
        </Text>
        <Text style={{ fontSize: 10, color: '#9CA3AF', marginTop: 40 }}>
          Version 2.0 | January 2026
        </Text>
      </View>

      <View style={{ marginTop: 80 }}>
        <Text style={styles.tocTitle}>Table of Contents</Text>
        <Text style={styles.tocItem}><Text style={styles.tocItemNumber}>1.</Text> Creating a Fixed Calendar</Text>
        <Text style={styles.tocItem}><Text style={styles.tocItemNumber}>2.</Text> Adding Services & Calendar Association</Text>
        <Text style={styles.tocItem}><Text style={styles.tocItemNumber}>3.</Text> Setting Availability</Text>
        <Text style={styles.tocItem}><Text style={styles.tocItemNumber}>4.</Text> Hide/Release Available Slots</Text>
        <Text style={styles.tocItem}><Text style={styles.tocItemNumber}>5.</Text> Adding a Customer</Text>
        <Text style={styles.tocItem}><Text style={styles.tocItemNumber}>6.</Text> Making a Booking for a Customer</Text>
        <Text style={styles.tocItem}><Text style={styles.tocItemNumber}>7.</Text> Editing and Canceling Bookings</Text>
        <Text style={styles.tocItem}><Text style={styles.tocItemNumber}>8.</Text> Booking Confirmation & Reminder Emails</Text>
        <Text style={styles.tocItem}><Text style={styles.tocItemNumber}>9.</Text> Sending Messages to Customers</Text>
        <Text style={styles.tocItem}><Text style={styles.tocItemNumber}>10.</Text> Reading Inbound Messages</Text>
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>yiBooking User Guide</Text>
        <Text style={styles.pageNumber}>Page 1</Text>
      </View>
    </Page>

    {/* Section 1: Creating a Fixed Calendar */}
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>
        <Text style={styles.sectionNumber}>1. </Text>Creating a Fixed Calendar
      </Text>
      
      <Text style={styles.paragraph}>
        A Fixed Calendar uses hourly time slots where appointments start at the top of each hour (9:00, 10:00, 11:00, etc.). This is ideal for services that have a standard 60-minute duration.
      </Text>

      <Text style={styles.subsectionTitle}>Steps to Create:</Text>
      
      <View style={styles.bulletList}>
        <NumberedItem number="1">From the Dashboard, click "My Calendars" or navigate to Dashboard → My Calendars</NumberedItem>
        <NumberedItem number="2">Click the "+ Add Calendar" button (top right)</NumberedItem>
        <NumberedItem number="3">Fill in the calendar details (see table below)</NumberedItem>
        <NumberedItem number="4">Click "Create Calendar"</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>Calendar Form Fields:</Text>
      
      <Table 
        headers={['Field', 'Description', 'Example']}
        rows={[
          ['Calendar Name*', 'Unique name for your calendar (cannot change later)', 'Pickleball Coaching'],
          ['Type*', 'Category of services you offer', 'Sports Coaching'],
          ['Location*', 'Physical address or virtual meeting info', '123 Main St, Mahwah NJ'],
          ['Timezone*', 'Your operating timezone (auto-detected)', 'Asia/Hong_Kong (GMT+8)'],
          ['Office Hours*', 'Business hours displayed to customers', '8am - 9pm'],
          ['Booking Type*', 'Fixed (hourly) or Floating (15-min intervals)', 'Fixed (Top-of-Hour)'],
          ['Min Advance Booking*', 'Hours in advance customers must book', '24 hours'],
          ['Max Advance Booking*', 'Days ahead customers can book', '14 days'],
          ['Cancellation Cutoff*', 'Hours before which customers can cancel', '48 hours'],
        ]}
      />

      <Note title="Important:">
        Fixed calendars require all associated services to have exactly 60-minute duration. When you select "Fixed (Top-of-Hour)" as the booking type, only 60-minute services can be assigned.
      </Note>

      <View style={styles.footer}>
        <Text style={styles.footerText}>yiBooking User Guide</Text>
        <Text style={styles.pageNumber}>Page 2</Text>
      </View>
    </Page>

    {/* Section 2: Adding Services */}
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>
        <Text style={styles.sectionNumber}>2. </Text>Adding Services & Calendar Association
      </Text>
      
      <Text style={styles.paragraph}>
        Services define what customers can book. You can associate services with calendars in three different ways.
      </Text>

      <Text style={styles.subsectionTitle}>Method A: During Service Creation</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Navigate to Dashboard → My Services</NumberedItem>
        <NumberedItem number="2">Click "+ Add Service"</NumberedItem>
        <NumberedItem number="3">Fill in service details and select calendar(s)</NumberedItem>
        <NumberedItem number="4">Click "Add Service"</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>Service Form Fields:</Text>
      <Table 
        headers={['Field', 'Description', 'Example']}
        rows={[
          ['Service Name*', 'Name shown to customers', 'Pickleball One-on-One Lesson'],
          ['Price*', 'Cost of the service', 'HK$500.00'],
          ['Duration*', 'Must be 60 min for Fixed calendars', '60 minutes'],
          ['Max Customers*', 'How many can book same slot', '1 (private) or 2-3 (group)'],
          ['Color Code', 'Visual identifier on calendar', 'Blue, Green, etc.'],
          ['Description', 'Details about the service', 'Perfect for beginners'],
          ['Add to Calendar(s)', 'Which calendar(s) to assign', 'First Calendar'],
        ]}
      />

      <Text style={styles.subsectionTitle}>Method B: Edit Existing Service</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Go to Dashboard → My Services</NumberedItem>
        <NumberedItem number="2">Click the Edit (pencil) icon for your service</NumberedItem>
        <NumberedItem number="3">Scroll to "Add to Calendar(s)" and check desired calendars</NumberedItem>
        <NumberedItem number="4">Click "Update Service"</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>Method C: From Edit Calendar Page</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Go to Dashboard → My Calendars</NumberedItem>
        <NumberedItem number="2">Click the Edit icon for your calendar</NumberedItem>
        <NumberedItem number="3">Click the "Calendar Services" tab</NumberedItem>
        <NumberedItem number="4">Check/uncheck services to associate</NumberedItem>
        <NumberedItem number="5">Click "Save Changes"</NumberedItem>
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>yiBooking User Guide</Text>
        <Text style={styles.pageNumber}>Page 3</Text>
      </View>
    </Page>

    {/* Section 3: Setting Availability */}
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>
        <Text style={styles.sectionNumber}>3. </Text>Setting Availability
      </Text>
      
      <Text style={styles.paragraph}>
        Availability tells the system when you're available to take bookings. Without setting availability, customers cannot book even if the calendar exists.
      </Text>

      <Text style={styles.subsectionTitle}>Creating Single-Day Availability:</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Open your calendar (click the Calendar icon in header)</NumberedItem>
        <NumberedItem number="2">Navigate to the date where you want to set availability</NumberedItem>
        <NumberedItem number="3">Right-click on any time slot</NumberedItem>
        <NumberedItem number="4">Select "Set Availability" from the context menu</NumberedItem>
        <NumberedItem number="5">Configure start time, end time, and options</NumberedItem>
        <NumberedItem number="6">Click "Set Availability"</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>Creating Recurring Availability:</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Right-click on a time slot and select "Set Availability"</NumberedItem>
        <NumberedItem number="2">Check "Repeat Availability"</NumberedItem>
        <NumberedItem number="3">Select days of the week (e.g., Mon, Wed, Fri)</NumberedItem>
        <NumberedItem number="4">Choose frequency (Weekly, Every 2 weeks, etc.)</NumberedItem>
        <NumberedItem number="5">Choose duration (One Month, Three Months, Forever)</NumberedItem>
        <NumberedItem number="6">Click "Set Availability"</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>Understanding Slot Colors:</Text>
      <Table 
        headers={['Color', 'Status', 'Meaning']}
        rows={[
          ['Green', 'Available', 'Open for customer bookings'],
          ['Yellow', 'On Hold', 'Visible to admin only, hidden from customers'],
          ['Blue', 'Booked', 'Has one or more appointments'],
          ['Gray', 'Past', 'Time has passed, no longer bookable'],
          ['White', 'No Availability', 'No availability set for this slot'],
        ]}
      />

      <Tip>Use the repeat availability feature to set up your regular schedule for weeks or months in advance, saving time on manual entry.</Tip>

      <View style={styles.footer}>
        <Text style={styles.footerText}>yiBooking User Guide</Text>
        <Text style={styles.pageNumber}>Page 4</Text>
      </View>
    </Page>

    {/* Section 4: Hide/Release Slots */}
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>
        <Text style={styles.sectionNumber}>4. </Text>Hide/Release Available Slots
      </Text>
      
      <Text style={styles.paragraph}>
        The On Hold feature lets you temporarily hide slots from customers without deleting the availability. This is useful for planning, reserving slots for VIPs, or managing demand.
      </Text>

      <Text style={styles.subsectionTitle}>Putting a Slot On Hold:</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Open your calendar in Week or Day view</NumberedItem>
        <NumberedItem number="2">Find an Available (green) slot</NumberedItem>
        <NumberedItem number="3">Right-click on the slot</NumberedItem>
        <NumberedItem number="4">Select "Hold This Slot" from the context menu</NumberedItem>
        <NumberedItem number="5">The slot turns yellow and is hidden from customers</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>Releasing a Held Slot:</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Find a Held (yellow) slot</NumberedItem>
        <NumberedItem number="2">Right-click on the slot</NumberedItem>
        <NumberedItem number="3">Select "Release Slot" from the context menu</NumberedItem>
        <NumberedItem number="4">Choose notification options (Email, SMS, or Both)</NumberedItem>
        <NumberedItem number="5">Select recipients (All customers, Specific customers, etc.)</NumberedItem>
        <NumberedItem number="6">Click "Release Slot"</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>Use Cases for On Hold:</Text>
      <View style={styles.bulletList}>
        <Bullet>Planning: Set up availability for future weeks while keeping them hidden</Bullet>
        <Bullet>VIP Bookings: Reserve slots for specific customers before opening to public</Bullet>
        <Bullet>Maintenance: Temporarily hide slots during equipment setup or breaks</Bullet>
        <Bullet>Gradual Release: Release slots in batches to manage demand</Bullet>
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>yiBooking User Guide</Text>
        <Text style={styles.pageNumber}>Page 5</Text>
      </View>
    </Page>

    {/* Section 5: Adding Customers */}
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>
        <Text style={styles.sectionNumber}>5. </Text>Adding a Customer
      </Text>
      
      <Text style={styles.paragraph}>
        Customers in yiBooking are scoped to specific calendars. This means each calendar has its own customer list, allowing you to manage different client bases.
      </Text>

      <Text style={styles.subsectionTitle}>Steps to Add a Customer:</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Navigate to Dashboard → Manage Customers</NumberedItem>
        <NumberedItem number="2">If you have multiple calendars, select the appropriate calendar</NumberedItem>
        <NumberedItem number="3">Click "+ Add Customer" (top right)</NumberedItem>
        <NumberedItem number="4">Fill in customer details</NumberedItem>
        <NumberedItem number="5">Click "Create"</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>Customer Form Fields:</Text>
      <Table 
        headers={['Field', 'Description', 'Required']}
        rows={[
          ['Full Name', 'Customer display name', 'Yes'],
          ['Email', 'Used for login and notifications', 'Yes'],
          ['Phone', 'Mobile number for SMS notifications', 'No'],
          ['Password', 'For customer portal login (min 6 chars)', 'Yes'],
          ['SMS Notifications', 'Enable SMS appointment reminders', 'No'],
        ]}
      />

      <Text style={styles.subsectionTitle}>Customer Actions:</Text>
      <Table 
        headers={['Icon', 'Action', 'Description']}
        rows={[
          ['💬', 'Message', 'Open message console to communicate'],
          ['👤', 'Run As', 'View app as this customer (admin preview)'],
          ['✏️', 'Edit', 'Update customer details'],
          ['🗑️', 'Delete', 'Remove customer from calendar'],
        ]}
      />

      <View style={styles.footer}>
        <Text style={styles.footerText}>yiBooking User Guide</Text>
        <Text style={styles.pageNumber}>Page 6</Text>
      </View>
    </Page>

    {/* Section 6: Making a Booking */}
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>
        <Text style={styles.sectionNumber}>6. </Text>Making a Booking for a Customer
      </Text>
      
      <Text style={styles.paragraph}>
        As an admin, you can create bookings on behalf of customers directly from the calendar view.
      </Text>

      <Text style={styles.subsectionTitle}>Steps to Create an Appointment:</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Open your calendar (Week or Day view)</NumberedItem>
        <NumberedItem number="2">Find an Available (green) slot at your desired date/time</NumberedItem>
        <NumberedItem number="3">Left-click on the slot to open the Create Appointment modal</NumberedItem>
        <NumberedItem number="4">Select a Service from the dropdown</NumberedItem>
        <NumberedItem number="5">Select a Customer from the dropdown</NumberedItem>
        <NumberedItem number="6">Add any optional notes</NumberedItem>
        <NumberedItem number="7">Click "Create Appointment"</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>For Multi-Customer Services (Group Bookings):</Text>
      <Text style={styles.paragraph}>
        If the selected service allows multiple customers (e.g., group lessons with Max Customers = 2 or 3):
      </Text>
      <View style={styles.bulletList}>
        <Bullet>After selecting the service, you'll see checkboxes instead of a dropdown</Bullet>
        <Bullet>Select multiple customers (up to the service limit)</Bullet>
        <Bullet>Each customer receives their own appointment and confirmation email</Bullet>
      </View>

      <Text style={styles.subsectionTitle}>What Happens After Booking:</Text>
      <View style={styles.bulletList}>
        <Bullet>The slot changes from green (Available) to blue (Booked)</Bullet>
        <Bullet>Customer receives a booking confirmation email with ICS calendar file</Bullet>
        <Bullet>Admin receives a copy of the confirmation</Bullet>
        <Bullet>The booking appears on both admin and customer calendars</Bullet>
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>yiBooking User Guide</Text>
        <Text style={styles.pageNumber}>Page 7</Text>
      </View>
    </Page>

    {/* Section 7: Editing and Canceling */}
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>
        <Text style={styles.sectionNumber}>7. </Text>Editing and Canceling Bookings
      </Text>

      <Text style={styles.subsectionTitle}>Editing a Booking:</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Open your calendar and find the booked slot (blue)</NumberedItem>
        <NumberedItem number="2">Left-click on the booking to open the Edit Appointment modal</NumberedItem>
        <NumberedItem number="3">Modify service, date/time, notes, or customers as needed</NumberedItem>
        <NumberedItem number="4">Click "Update Appointment"</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>Managing Multi-Customer Bookings:</Text>
      <View style={styles.bulletList}>
        <Bullet>You'll see all current participants listed</Bullet>
        <Bullet>Uncheck a customer to remove them (they receive cancellation notice)</Bullet>
        <Bullet>Check additional customers to add them (only new ones receive confirmation)</Bullet>
        <Bullet>Existing customers are NOT re-notified when you add new participants</Bullet>
      </View>

      <Text style={styles.subsectionTitle}>Canceling a Booking:</Text>
      <Text style={{ ...styles.paragraph, fontWeight: 'bold' }}>Method 1: From Edit Modal</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Click on the booked slot to open Edit Appointment</NumberedItem>
        <NumberedItem number="2">Click "Cancel Appointment" (red button, bottom left)</NumberedItem>
        <NumberedItem number="3">Confirm the cancellation</NumberedItem>
      </View>

      <Text style={{ ...styles.paragraph, fontWeight: 'bold' }}>Method 2: From Context Menu</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Right-click on a booked slot</NumberedItem>
        <NumberedItem number="2">Select "Cancel Appointment"</NumberedItem>
        <NumberedItem number="3">Confirm the cancellation</NumberedItem>
      </View>

      <Note>When a booking is cancelled, the customer automatically receives a cancellation notification email, and the slot returns to Available (green) status.</Note>

      <View style={styles.footer}>
        <Text style={styles.footerText}>yiBooking User Guide</Text>
        <Text style={styles.pageNumber}>Page 8</Text>
      </View>
    </Page>

    {/* Section 8: Confirmation and Reminder Emails */}
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>
        <Text style={styles.sectionNumber}>8. </Text>Booking Confirmation & Reminder Emails
      </Text>
      
      <Text style={styles.paragraph}>
        yiBooking automatically sends email notifications throughout the booking lifecycle to keep customers informed.
      </Text>

      <Text style={styles.subsectionTitle}>Email Types:</Text>
      <Table 
        headers={['Email Type', 'When Sent', 'Contents']}
        rows={[
          ['Confirmation', 'Immediately after booking', 'Date, time, service, location, ICS file'],
          ['Reminder', 'Configurable (default: 24h before)', 'Same as confirmation + reminder text'],
          ['Modification', 'When booking is changed', 'Updated appointment details'],
          ['Cancellation', 'When booking is cancelled', 'Cancellation confirmation'],
        ]}
      />

      <Text style={styles.subsectionTitle}>Customizing Email Templates:</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Navigate to Dashboard → Branding & Email</NumberedItem>
        <NumberedItem number="2">Scroll to Email Templates section</NumberedItem>
        <NumberedItem number="3">Select template type (Confirmation, Reminder, etc.)</NumberedItem>
        <NumberedItem number="4">Customize using available variables</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>Available Email Variables:</Text>
      <Table 
        headers={['Variable', 'Description']}
        rows={[
          ['&customer-name', "Customer's full name"],
          ['&service-name', 'Name of the booked service'],
          ['&service-price', 'Price of the service'],
          ['&appointment-date', 'Date of the appointment'],
          ['&appointment-time', 'Time of the appointment'],
          ['&calendar-name', 'Name of the calendar'],
          ['&calendar-location', 'Location of the calendar'],
          ['&booked-by', 'Who made the booking (Admin/Customer)'],
          ['&co-participants', 'Other customers in multi-person bookings'],
        ]}
      />

      <View style={styles.footer}>
        <Text style={styles.footerText}>yiBooking User Guide</Text>
        <Text style={styles.pageNumber}>Page 9</Text>
      </View>
    </Page>

    {/* Section 9: Sending Messages */}
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>
        <Text style={styles.sectionNumber}>9. </Text>Sending Messages to Customers
      </Text>
      
      <Text style={styles.paragraph}>
        yiBooking provides a unified Message Console for communicating with customers across multiple channels.
      </Text>

      <Text style={styles.subsectionTitle}>Opening the Message Console:</Text>
      <View style={styles.bulletList}>
        <Bullet>From Manage Customers: Click the Message (chat) icon for any customer</Bullet>
        <Bullet>From Calendar Booking: Click on a booking, then "Send Message to Customer"</Bullet>
        <Bullet>From Context Menu: Right-click on a booking and select "Send Message"</Bullet>
      </View>

      <Text style={styles.subsectionTitle}>Message Channels:</Text>
      <Table 
        headers={['Channel', 'Description', 'Requirements']}
        rows={[
          ['yiBooking', 'In-app real-time messaging', 'Customer must log in to see'],
          ['Email', 'Standard email to customer', 'Valid email address'],
          ['SMS', 'Text message to phone', 'Phone number + SMS consent'],
        ]}
      />

      <Text style={styles.subsectionTitle}>Sending a Message:</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Open the Message Console for a customer</NumberedItem>
        <NumberedItem number="2">Select your channel tab (yiBooking, Email, or SMS)</NumberedItem>
        <NumberedItem number="3">For Email: Enter a subject line</NumberedItem>
        <NumberedItem number="4">Type your message</NumberedItem>
        <NumberedItem number="5">Click "Send"</NumberedItem>
      </View>

      <Tip>Your recent email subjects are saved for quick reuse. SMS messages are limited to 160 characters.</Tip>

      <View style={styles.footer}>
        <Text style={styles.footerText}>yiBooking User Guide</Text>
        <Text style={styles.pageNumber}>Page 10</Text>
      </View>
    </Page>

    {/* Section 10: Reading Inbound Messages */}
    <Page size="A4" style={styles.page}>
      <Text style={styles.sectionTitle}>
        <Text style={styles.sectionNumber}>10. </Text>Reading Inbound Messages
      </Text>
      
      <Text style={styles.paragraph}>
        The Message Console shows a unified, chronological thread of all communications with a customer across all channels.
      </Text>

      <Text style={styles.subsectionTitle}>Viewing the Conversation:</Text>
      <View style={styles.bulletList}>
        <NumberedItem number="1">Open the Message Console for any customer</NumberedItem>
        <NumberedItem number="2">All messages appear in time order</NumberedItem>
        <NumberedItem number="3">Your messages (outbound) appear on the right side</NumberedItem>
        <NumberedItem number="4">Customer messages (inbound) appear on the left side</NumberedItem>
      </View>

      <Text style={styles.subsectionTitle}>Message Thread Features:</Text>
      <View style={styles.bulletList}>
        <Bullet>Channel Icons: Quickly identify which channel each message used</Bullet>
        <Bullet>Timestamps: See exactly when messages were sent/received</Bullet>
        <Bullet>Collapsible Emails: Long email threads are collapsed by default</Bullet>
        <Bullet>Online Indicator: Green dot shows if customer is currently online</Bullet>
        <Bullet>Read Receipts: See when your yiBooking messages were read</Bullet>
      </View>

      <Text style={styles.subsectionTitle}>Notification Bell:</Text>
      <Text style={styles.paragraph}>
        The bell icon in the header provides quick access to recent messages. It shows the unread message count and clicking it reveals recent messages across all customers. Click any message preview to open the full conversation.
      </Text>

      <View style={{ marginTop: 30, padding: 15, backgroundColor: '#F3F4F6', borderRadius: 5 }}>
        <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#1F2937', marginBottom: 8 }}>Need More Help?</Text>
        <Text style={{ fontSize: 10, color: '#4B5563', marginBottom: 4 }}>• Click the Support icon (?) in the header for live chat</Text>
        <Text style={{ fontSize: 10, color: '#4B5563', marginBottom: 4 }}>• Visit the FAQ page for common questions</Text>
        <Text style={{ fontSize: 10, color: '#4B5563' }}>• Email: support@yibooking.com</Text>
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>yiBooking User Guide</Text>
        <Text style={styles.pageNumber}>Page 11</Text>
      </View>
    </Page>
  </Document>
);

// Function to generate and download PDF
export const generateUserGuidePDF = async () => {
  const blob = await pdf(<UserGuidePDF />).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'yiBooking_Fixed_Calendar_Admin_User_Guide.pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default UserGuidePDF;
